import { useState } from 'react';

import useTranslation from 'components/FloatingResourceAddMenu/hooks/useTranslation';
import CreateApplicationForm from 'components/forms/CreateApplicationForm';
import CreateAssetForm from 'components/forms/CreateAssetForm';
import CreateBoundaryForm from 'components/forms/CreateBoundaryForm';
import CreateClientForm from 'components/forms/CreateClientForm';
import CreateFarmForm from 'components/forms/CreateFarmForm';
import CreateFieldForm from 'components/forms/CreateFieldForm';
import InviteUserForm from 'components/forms/InviteUserForm';

import { FLOATING_RESOURCE_ADD_MENU_OPTION } from './constants';
import type { FloatingResourceAddOption, Props } from './types';

const useData = ({ defaultAssetType, defaultResource, onClose, openRequest }: Props) => {
    const {
        APPLICATION_LABEL,
        ASSET_LABEL,
        BOUNDARY_LABEL,
        CLIENT_LABEL,
        FARM_LABEL,
        FIELD_LABEL,
        MEMBER_LABEL
    } = useTranslation();

    const [selectedResourceValue, setSelectedResourceValue] =
        useState<FLOATING_RESOURCE_ADD_MENU_OPTION | null>(
            openRequest?.resource ?? defaultResource ?? null
        );

    const [requestingClose, setRequestingClose] = useState(false);

    const handleRequestClose = () => {
        if (selectedResourceValue) {
            setRequestingClose(true);
        } else {
            onClose();
        }
    };

    const handleCancelClose = () => {
        setRequestingClose(false);
    };

    const handleConfirmedClose = () => {
        setSelectedResourceValue(null);
        setRequestingClose(false);
    };

    const handleResourceCreated = () => {
        setSelectedResourceValue(null);
        setRequestingClose(false);
        onClose();
    };

    const handleBackToMenu = () => {
        setRequestingClose(true);
    };

    const handleSelectResource = (resourceValue: FLOATING_RESOURCE_ADD_MENU_OPTION) => {
        setSelectedResourceValue(resourceValue);
        setRequestingClose(false);
    };

    const farmDefaultValues =
        openRequest?.resource === FLOATING_RESOURCE_ADD_MENU_OPTION.FARM
            ? openRequest.defaultValues
            : undefined;

    const fieldDefaultValues =
        openRequest?.resource === FLOATING_RESOURCE_ADD_MENU_OPTION.FIELD
            ? openRequest.defaultValues
            : undefined;

    const boundaryDefaultValues =
        openRequest?.resource === FLOATING_RESOURCE_ADD_MENU_OPTION.BOUNDARY
            ? openRequest.defaultValues
            : undefined;

    const menuOptions: FloatingResourceAddOption[] = [
        {
            label: CLIENT_LABEL,
            renderComponent: () => (
                <CreateClientForm
                    onCancelClose={handleCancelClose}
                    onClose={handleConfirmedClose}
                    onCreated={handleResourceCreated}
                    requestingClose={requestingClose}
                />
            ),
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.CLIENT
        },
        {
            label: FARM_LABEL,
            renderComponent: () => (
                <CreateFarmForm
                    defaultValues={farmDefaultValues}
                    onCancelClose={handleCancelClose}
                    onClose={handleConfirmedClose}
                    onCreated={handleResourceCreated}
                    requestingClose={requestingClose}
                />
            ),
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.FARM
        },
        {
            label: MEMBER_LABEL,
            renderComponent: () => (
                <InviteUserForm
                    onCancelClose={handleCancelClose}
                    onClose={handleConfirmedClose}
                    onCreated={handleResourceCreated}
                    requestingClose={requestingClose}
                />
            ),
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.MEMBER
        },
        {
            label: FIELD_LABEL,
            renderComponent: () => (
                <CreateFieldForm
                    defaultValues={fieldDefaultValues}
                    onCancelClose={handleCancelClose}
                    onClose={handleConfirmedClose}
                    onCreated={handleResourceCreated}
                    requestingClose={requestingClose}
                />
            ),
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.FIELD
        },

        {
            label: ASSET_LABEL,
            renderComponent: () => (
                <CreateAssetForm
                    defaultAssetType={defaultAssetType}
                    onCancelClose={handleCancelClose}
                    onClose={handleConfirmedClose}
                    onCreated={handleResourceCreated}
                    requestingClose={requestingClose}
                />
            ),
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.ASSET
        },
        {
            label: APPLICATION_LABEL,
            renderComponent: () => (
                <CreateApplicationForm
                    onCancelClose={handleCancelClose}
                    onClose={handleConfirmedClose}
                    onCreated={handleResourceCreated}
                    requestingClose={requestingClose}
                />
            ),
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.APPLICATION
        },
        {
            label: BOUNDARY_LABEL,
            renderComponent: () => (
                <CreateBoundaryForm
                    defaultValues={boundaryDefaultValues}
                    onCancelClose={handleCancelClose}
                    onClose={handleConfirmedClose}
                    onCreated={handleResourceCreated}
                    requestingClose={requestingClose}
                />
            ),
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.BOUNDARY
        }
    ];

    const selectedResource =
        menuOptions.find(({ value }) => value === selectedResourceValue) ?? null;

    return {
        handleBackToMenu,
        handleRequestClose,
        handleSelectResource,
        menuOptions,
        selectedResource
    };
};

export default useData;
